import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import {
  getRetryDelayMs,
  is429Error,
  MAX_RETRIES,
  waitWithAbort,
} from './retry429';

export function createBaseQueryWithRetry(
  baseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    {},
    FetchBaseQueryMeta
  >
): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  {},
  FetchBaseQueryMeta
> {
  return async (args, api, extraOptions) => {
    const retry429 = async (
      result: Awaited<ReturnType<typeof baseQuery>>,
      attempt: number
    ): Promise<Awaited<ReturnType<typeof baseQuery>>> => {
      if (!is429Error(result.error) || attempt > MAX_RETRIES) {
        return result;
      }

      const retryDelayMs = getRetryDelayMs(result.meta, attempt);
      const shouldContinue = await waitWithAbort(retryDelayMs, api.signal);

      if (!shouldContinue) {
        return result;
      }

      const nextResult = await baseQuery(args, api, extraOptions);
      return retry429(nextResult, attempt + 1);
    };

    const result = await baseQuery(args, api, extraOptions);
    return retry429(result, 1);
  };
}
